const React = require('react');
const { default: TextField } = require('@mui/material/TextField');
const { default: Styled, css } = require('styled-components');
const { singleLineFieldStyles } = require('./singleLineFieldStyles');
const ClearFieldAdornment = require('./ClearFieldAdornment');
const FieldContext = require('./FieldContext');

const internals = {};

internals.hasClearValue = (value) => value !== null
    && typeof value !== 'undefined'
    && String(value).length > 0;

internals.normalizeRequiredLabel = ({
    label,
    required
}) => {

    if (!required || typeof label !== 'string') {
        return label;
    }

    return label.replace(/\s*\*+\s*$/, '');
};

module.exports = function FormTextField(props) {
    const { SingleLineTextField } = internals;

    const {
        field = {},
        fieldName = field.name,
        label = field.label,
        onChange,
        onBlur,
        onFocus,
        setValue,
        textFieldProps = {},
        useField: requestedUseFieldHook,
        value
    } = props;

    const resolvedUseField = typeof requestedUseFieldHook === 'function'
        ? requestedUseFieldHook
        : FieldContext.useField;

    const fieldController = resolvedUseField(fieldName);
    const resolvedValue = typeof value === 'undefined'
        ? fieldController.getValue() || ''
        : value;
    const resolvedTextFieldProps = fieldController.getTextFieldProps?.(textFieldProps) || textFieldProps;
    const {
        fieldStyles,
        ...resolvedDomTextFieldProps
    } = resolvedTextFieldProps;
    const hasError = !!resolvedTextFieldProps.error;
    const helperText = resolvedTextFieldProps.helperText;
    const canClear = !!field.clearable;
    const hasClearValue = internals.hasClearValue(resolvedValue);
    const clearLabel = field.clearLabel || `Clear ${label || fieldName}`;
    const setFieldValue = typeof setValue === 'function'
        ? setValue
        : fieldController.setValue;
    const required = !!(field.required || resolvedDomTextFieldProps.required);

    const clearFieldValue = (event) => {

        if (!hasClearValue) {
            return;
        }

        if (typeof setValue === 'function') {
            setValue('', event);
            return;
        }

        fieldController.setValue('', undefined, event);
    };

    const mergedInputProps = {
        ...(resolvedTextFieldProps.InputProps || {}),
        endAdornment: canClear ? <ClearFieldAdornment
            clearLabel={clearLabel}
            disabled={resolvedTextFieldProps.disabled || !hasClearValue}
            onClear={clearFieldValue}
            existingEndAdornment={resolvedTextFieldProps.InputProps?.endAdornment}
        /> : resolvedTextFieldProps.InputProps?.endAdornment
    };

    if (typeof window !== 'undefined' && window.__USER_INPUTS_DEBUG_FIELDS__) {
        // eslint-disable-next-line no-console
        console.log('[__USER_INPUTS_DEBUG__ TextField props]', fieldName, {
            fieldError: fieldController.getError?.(),
            hasError,
            helperText,
            resolvedTextFieldProps
        });
    }

    return <SingleLineTextField
        fullWidth={typeof field.fullWidth === 'undefined' ? true : field.fullWidth}
        label={internals.normalizeRequiredLabel({
            label,
            required
        })}
        placeholder={field.placeholder}
        {...resolvedDomTextFieldProps}
        required={required}
        InputProps={mergedInputProps}
        $fieldStyles={fieldStyles}
        value={resolvedValue}
        error={hasError}
        helperText={helperText}
        inputProps={{
            ...(resolvedTextFieldProps.inputProps || {}),
            'aria-invalid': hasError || undefined
        }}
        onBlur={(event) => {
            fieldController.onBlur?.(event);
            onBlur?.(event);
        }}
        onChange={(event) => {

            let nextValue = event.target.value;

            if (typeof onChange === 'function') {
                const returnedValue = onChange(nextValue, event);

                if (typeof returnedValue !== 'undefined') {
                    nextValue = returnedValue;
                }
            }

            setFieldValue(nextValue, event);
        }}
        onFocus={(event) => {
            fieldController.onFocus?.(event);
            onFocus?.(event);
        }}
    />;
};

internals.SingleLineTextField = Styled(TextField)`
    ${singleLineFieldStyles}

    & .MuiInputBase-root {
        align-items: center;
        flex-wrap: nowrap !important;
        padding-right: 0 !important;
    }

    ${({ $fieldStyles }) => css`${$fieldStyles || ''}`}
`;
